import * as React from 'react';

import { assertNever } from '@gitbook/util-ts';

import { ContentKitBlock } from './ContentKitBlock';
import { ContentKitBox } from './ContentKitBox';
import { ContentKitButton } from './ContentKitButton';
import { ContentKitCard } from './ContentKitCard';
import { ContentKitCheckbox } from './ContentKitCheckbox';
import { ContentKitCodeBlock } from './ContentKitCodeBlock';
import { ContentKitConfiguration } from './ContentKitConfiguration';
import { ContentKitDivider } from './ContentKitDivider';
import { ContentKitHint } from './ContentKitHint';
import { ContentKitImage, ContentKitImageRenderContext } from './ContentKitImage';
import { ContentKitInput } from './ContentKitInput';
import { ContentKitLink } from './ContentKitLink';
import { ContentKitMarkdown } from './ContentKitMarkdown';
import { ContentKitModal } from './ContentKitModal';
import { ContentKitRadio } from './ContentKitRadio';
import { ContentKitSelect, type ContentKitSelectSourceOpenAPISpec } from './ContentKitSelect';
import { ContentKitStack } from './ContentKitStack';
import { ContentKitStepper, ContentKitStepperStep } from './ContentKitStepper';
import { ContentKitSwitch } from './ContentKitSwitch';
import { ContentKitText } from './ContentKitText';
import { ContentKitTextInput } from './ContentKitTextInput';
import { ContentKitWebFrame } from './ContentKitWebFrame';
import type { ContentKitElementProps, ContentKitElementRef, ContentKitElementType } from './types';

export interface ContentKitRemoteComponents {
    'select-source-openapi': (
        props: ContentKitElementProps<ContentKitSelectSourceOpenAPISpec>
    ) => React.ReactElement;
}

/**
 * Render a ContentKit element as a React element.
 */
export const ContentKitElement = React.forwardRef<
    ContentKitElementRef,
    Omit<ContentKitElementProps<ContentKitElementType>, 'renderElement' | 'renderElements'>
>(function ContentKitElementWithRef(props, ref): React.ReactElement | null {
    const { element } = props;

    if (typeof element === 'string' || typeof element === 'number') {
        return <>{element}</>;
    }

    const elementProps: ContentKitElementProps<ContentKitElementType> = {
        ...props,
        renderElement: (childElement, ref, key) => (
            <ContentKitElement key={key} ref={ref} {...props} element={childElement} />
        ),
        renderElements: (display, childElements) => {
            const nodes = childElements.map((child, index) => {
                const content = elementProps.renderElement(child, undefined, index);
                return content;
            });

            return (
                <ContentKitImageRenderContext.Provider value={display}>
                    {nodes}
                </ContentKitImageRenderContext.Provider>
            );
        },
    };

    switch (element.type) {
        case 'text':
            return <ContentKitText {...elementProps} element={element} />;
        case 'button':
            return <ContentKitButton {...elementProps} element={element} />;
        case 'textinput':
            return <ContentKitTextInput {...elementProps} element={element} />;
        case 'block':
            return <ContentKitBlock {...elementProps} element={element} />;
        case 'configuration':
            return <ContentKitConfiguration {...elementProps} element={element} />;
        case 'hstack':
            return <ContentKitStack {...elementProps} element={element} />;
        case 'vstack':
            return <ContentKitStack {...elementProps} element={element} />;
        case 'divider':
            return <ContentKitDivider {...elementProps} element={element} />;
        case 'webframe':
            return <ContentKitWebFrame {...elementProps} element={element} />;
        case 'codeblock':
            return <ContentKitCodeBlock {...elementProps} element={element} />;
        case 'markdown':
            return <ContentKitMarkdown {...elementProps} element={element} />;
        case 'hint':
            return <ContentKitHint {...elementProps} element={element} />;
        case 'box':
            return <ContentKitBox {...elementProps} element={element} />;
        case 'modal':
            return <ContentKitModal {...elementProps} element={element} />;
        case 'card':
            return <ContentKitCard {...elementProps} element={element} />;
        case 'image':
            return <ContentKitImage {...elementProps} element={element} />;
        case 'input':
            return <ContentKitInput {...elementProps} element={element} />;
        case 'select': {
            return <ContentKitSelect {...elementProps} element={element} />;
        }
        case 'switch':
            return <ContentKitSwitch ref={ref} {...elementProps} element={element} />;
        case 'radio':
            return <ContentKitRadio ref={ref} {...elementProps} element={element} />;
        case 'checkbox':
            return <ContentKitCheckbox ref={ref} {...elementProps} element={element} />;
        case 'link':
            return <ContentKitLink {...elementProps} element={element} />;
        case 'stepper':
            return <ContentKitStepper {...elementProps} element={element} />;
        case 'step':
            return <ContentKitStepperStep {...elementProps} element={element} />;
        default:
            assertNever(element);
    }
});
